/**
 * The day editor (B-14) — the correction surface, and the reason the dashboard exists.
 *
 * Everything the bot cannot express lives here: the per-set `supported` toggle that
 * shorthand deliberately omits (D-25), moving a day to a different date, and changing
 * the bodyweight a day was scored against.
 *
 * It is also where a day is *started* by hand (B-23). The bot is the fast path for
 * capture, but it is a dependency on Meta and it has gone quiet before (C-15, C-22), so
 * the portal must be able to log a session on its own. Starting a day and correcting one
 * are the same act — same picker, same set rows, same single write — so this is one
 * screen with a null `base` rather than a second one.
 *
 * The editing model is a draft. Changes accumulate locally and land in one write, so a
 * half-finished correction is never persisted and the running score updates as you type
 * without touching Firestore. Saving is a compare-and-set against `updatedAt` (D-60):
 * if the bot wrote to this day meanwhile, the save is refused rather than silently
 * erasing it.
 */

import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import type { CatalogExercise, LoggedExercise, Workout } from "@altrtrack/core";
import {
  DEFAULT_BODYWEIGHT_KG,
  emptyWorkout,
  exerciseById,
  formatNumber,
  isDateString,
  plural,
  scoreWorkout,
} from "@altrtrack/core";
import { localToday, useProfile, useWorkouts } from "../data.js";
import { deleteDay, saveDay, type SaveResult } from "../write.js";
import { ExercisePicker } from "../components/ExercisePicker.js";
import { ExerciseEditor } from "../components/ExerciseEditor.js";
import { NumberField } from "../components/NumberField.js";
import { BODYWEIGHT, checkDate } from "../fields.js";
import { relativeDay } from "../relative.js";

/**
 * A logged exercise whose catalogId is not in the catalog scores 0 (D-34). The editor
 * has to render it anyway rather than crash — the data exists, and this screen is how
 * someone would fix it.
 */
const UNKNOWN_EXERCISE = { bodyweightFactor: 0, measure: "reps" } as const;

/**
 * A different date is a different editor.
 *
 * Saving a day under a new date navigates to that date, and without the key React keeps
 * this component mounted with the draft and the `base` it was opened with — so a day
 * that has just been written lands on a page still holding the old `base`, which reads
 * as "this day changed elsewhere" and refuses to save. Remounting is the honest answer:
 * none of the state below survives a change of date, so none of it should.
 */
export function DayPage({ uid }: { uid: string }) {
  const { date = "" } = useParams();
  return <DayEditor key={date} uid={uid} date={date} />;
}

function DayEditor({ uid, date }: { uid: string; date: string }) {
  const navigate = useNavigate();
  /*
   * Where this editor was opened from, so leaving it goes back there. A missed day tapped
   * on Progress and then logged should land on Progress, where the stub has just become a
   * bar — not on Today, which says nothing about the day that was filled in (B-61).
   */
  const state = useLocation().state as { from?: string } | null;
  const back = state?.from === "progress" ? "/progress" : "/";
  const workouts = useWorkouts(uid);
  const profile = useProfile(uid);

  const stored = workouts.data?.find((w) => w.date === date) ?? null;
  const [draft, setDraft] = useState<Workout | null>(null);
  /*
   * The `updatedAt` this draft was made from — or `null`, meaning "this day does not
   * exist yet and I am creating it". `undefined` is the third state, and a distinct one:
   * nothing has been adopted at all, so the page is still loading. Collapsing null and
   * undefined here would make a day that is still loading look like a day being created,
   * and Save would try to create over the top of it.
   */
  const [base, setBase] = useState<number | null | undefined>(undefined);
  const [picking, setPicking] = useState(false);
  const [problem, setProblem] = useState<SaveResult | null>(null);
  const [saving, setSaving] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  /*
   * The date field's own text, separate from the draft's date.
   *
   * A native date input reports a half-entered date as "" — pick a month and the value is
   * empty until the day is chosen too. The old handler was `isDateString(v) && update(v)`,
   * which silently swallowed those intermediate states and every out-of-range one, so a
   * date typed as 2027 simply did not take and nothing said why.
   */
  const [dateDraft, setDateDraft] = useState<string | null>(null);

  const settled = !workouts.loading && !profile.loading;
  // The user's own day, not the browser's. Resolved before the effect below so that the
  // effect and the render agree about which date counts as today.
  const today = localToday(profile.data?.timezone ?? "UTC");

  // Adopt the stored day as the draft once, and again only if it changes underneath a
  // draft that has no unsaved work in it.
  useEffect(() => {
    if (stored) {
      setDraft((current) => (current === null ? stored : current));
      setBase((current) => (current === undefined ? stored.updatedAt : current));
      return;
    }
    /*
     * Nothing logged on this date, so it is a day to fill in (B-23) — today, or any past
     * day that was trained and never logged (B-61, D-113). This used to be today only, on
     * the theory that an empty past date was more likely a mistyped URL than a backfill;
     * but Progress sends a tapped missed day here (B-58), and that sent it to a dead end.
     * A mistyped date costs nothing: nothing is written until Save, and Save needs a set.
     * A future or impossible date still gets `Missing` — `checkDate` is the same rule the
     * date field enforces, so the editor never opens on a date it would refuse to save.
     *
     * Waiting for `settled` matters twice over: an unloaded workout list looks exactly
     * like an empty one, and an unloaded profile would resolve `today` in UTC and hand
     * out a blank draft dated to the wrong day.
     */
    if (!settled || !isDateString(date) || checkDate(date, today) !== null) return;
    setDraft((current) => current ?? emptyWorkout(date, profile.data?.currentBodyweightKg ?? DEFAULT_BODYWEIGHT_KG, 0));
    setBase((current) => (current === undefined ? null : current));
  }, [stored, settled, date, today, profile.data?.currentBodyweightKg]);

  if (!draft || base === undefined) {
    return settled ? <Missing date={date} /> : <p className="sub">Loading…</p>;
  }

  /** No document behind this draft yet — Save will create one rather than overwrite. */
  const creating = base === null;
  const shownDate = dateDraft ?? draft.date;
  const dateProblem = checkDate(shownDate, today);

  const dirty = stored === null || JSON.stringify(draft) !== JSON.stringify(stored);
  // A day with no exercises in it is not a day, unless it is marked PT (D-107): the bot
  // keeps such a day too, so it has to be something this editor can save.
  const empty = draft.exercises.length === 0 && draft.pt !== true;
  const movedOn = stored !== null && stored.updatedAt !== base;
  const score = scoreWorkout(draft);
  const sets = draft.exercises.reduce((n, e) => n + e.sets.length, 0);

  const update = (change: Partial<Workout>) => setDraft({ ...draft, ...change });

  const addExercise = (exercise: CatalogExercise) => {
    const added: LoggedExercise = {
      id: crypto.randomUUID(),
      catalogId: exercise.id,
      name: exercise.name,
      muscleGroup: exercise.muscleGroup,
      sets: [{ id: crypto.randomUUID(), reps: exercise.measure === "duration" ? 60 : 10, weightKg: 0, supported: false }],
    };
    // First, not last: the button is above the list (D-95), so the card lands under it.
    update({ exercises: [added, ...draft.exercises] });
    setPicking(false);
  };

  const save = async () => {
    setSaving(true);
    setProblem(null);
    const result = await saveDay(uid, date, draft, base);
    setSaving(false);
    if (!result.ok) {
      setProblem(result);
      return;
    }
    if (draft.date === date) navigate(back, { replace: true });
    else navigate(`/day/${draft.date}`, { replace: true, state });
  };

  return (
    <>
      <Link className="crumb" to={back}>
        {back === "/progress" ? "← Progress" : "← Today"}
      </Link>

      <div className="editor-head">
        <h1 className="title">{relativeDay(draft.date, today)}</h1>
        <p className="sub">
          {draft.exercises.length} {plural(draft.exercises.length, "exercise")}, {sets}{" "}
          {plural(sets, "set")} · <span className="num">{formatNumber(score)}</span>
        </p>
      </div>

      {creating && empty && (
        <p className="sub starting-hint">
          Add every exercise you did, then log the day in one go. It scores exactly as it
          would have done through the bot.
        </p>
      )}

      {movedOn && (
        <div className="notice bad">
          <b>This day changed elsewhere.</b>
          <p className="sub">
            The bot logged something here while you were editing. Saving would erase it, so
            it will be refused. Discard your changes to see the new version.
          </p>
          <button
            className="btn btn-small"
            type="button"
            onClick={() => {
              setDraft(stored);
              setBase(stored.updatedAt);
              // The date field holds its own text, so discarding has to clear that too or
              // a rejected date survives the thing that was meant to undo it.
              setDateDraft(null);
            }}
          >
            Discard my changes
          </button>
        </div>
      )}

      <section className="field-row">
        <label className="field">
          <span className="label">Date</span>
          <input
            className="input"
            type="date"
            value={shownDate}
            max={today}
            aria-invalid={dateProblem !== null}
            aria-describedby="date-problem"
            onChange={(e) => {
              setDateDraft(e.target.value);
              // The draft only moves for a date that is real, in range, and not ahead of
              // today. Anything else stays in `dateDraft` with the reason underneath it.
              if (isDateString(e.target.value) && checkDate(e.target.value, today) === null) {
                update({ date: e.target.value });
              }
            }}
          />
          {dateProblem && (
            <p className="field-error" id="date-problem" role="status">
              {dateProblem}
            </p>
          )}
        </label>
        <label className="field">
          <span className="label">Bodyweight (kg)</span>
          <NumberField
            className="input input-narrow"
            value={draft.bodyweightKg}
            bounds={BODYWEIGHT}
            label="Bodyweight this day, in kilograms"
            onCommit={(bodyweightKg) => update({ bodyweightKg })}
          />
        </label>
      </section>
      <p className="sub spaced">
        Changing the bodyweight rescores this day only. Other days keep the weight they
        were logged at.
      </p>
      {/* Its own line, above Add an exercise as on Today (B-56). Inside the date and
          bodyweight row it top-aligned with the Date label, not the input, and pushed the
          bodyweight onto a second line at 393px. */}
      <label className="check">
        <input
          type="checkbox"
          checked={draft.pt === true}
          onChange={(e) => {
            if (e.target.checked) {
              update({ pt: true });
            } else {
              // Remove the key; "no" has one shape and Firestore rejects undefined.
              const { pt: _pt, ...rest } = draft;
              setDraft(rest);
            }
          }}
        />
        <span>PT session</span>
      </label>

      {/* Above the exercises, as on Today (D-95). */}
      {picking ? (
        <ExercisePicker onPick={addExercise} onCancel={() => setPicking(false)} />
      ) : (
        <button className="btn spaced" type="button" onClick={() => setPicking(true)}>
          Add an exercise
        </button>
      )}

      <div className="exercises">
        {draft.exercises.map((exercise, index) => (
          <ExerciseEditor
            key={exercise.id}
            exercise={exercise}
            catalog={exerciseById(exercise.catalogId) ?? UNKNOWN_EXERCISE}
            bodyweightKg={draft.bodyweightKg}
            onChange={(next) =>
              update({ exercises: draft.exercises.map((e, i) => (i === index ? next : e)) })
            }
            onRemove={() => update({ exercises: draft.exercises.filter((_, i) => i !== index) })}
          />
        ))}
      </div>

      {problem && !problem.ok && (
        <div className="notice bad">
          {problem.reason === "changed" && <b>This day changed while you were editing.</b>}
          {problem.reason === "occupied" && (
            <b>You already have a day logged on {problem.date}. Move it somewhere else, or edit that day instead.</b>
          )}
          {problem.reason === "failed" && (
            <>
              <b>Couldn't save.</b>
              <p className="sub">{problem.detail}</p>
            </>
          )}
        </div>
      )}

      <div className="actions">
        <button
          className="btn btn-primary"
          type="button"
          onClick={save}
          disabled={!dirty || empty || saving || movedOn || dateProblem !== null}
        >
          {saving ? "Saving…" : creating ? "Log this day" : dirty ? "Save changes" : "Saved"}
        </button>
        {creating ? null : confirmingDelete ? (
          <span className="confirm">
            <span className="sub">Delete this whole day?</span>
            <button
              className="btn btn-danger"
              type="button"
              onClick={async () => {
                await deleteDay(uid, date);
                navigate(back, { replace: true });
              }}
            >
              Delete it
            </button>
            <button className="btn" type="button" onClick={() => setConfirmingDelete(false)}>
              Keep it
            </button>
          </span>
        ) : (
          <button className="btn-link-danger" type="button" onClick={() => setConfirmingDelete(true)}>
            Delete day
          </button>
        )}
      </div>
    </>
  );
}

function Missing({ date }: { date: string }) {
  return (
    <div className="centered">
      <h1 className="title">Nothing to log on {date}.</h1>
      <p className="sub">
        A day can be logged for today or any day before it. To fill in a session you missed,
        tap that day on Progress.
      </p>
      <p className="sub">
        <Link to="/">Back to today</Link>
      </p>
    </div>
  );
}
